'use client';

import * as Styled from './OrganizationList.styled';
import { OrganizationRedirectModal } from './OrganizationRedirectModal';
import { CreateOrganization } from '../CreateOrganization/CreateOrganization';
import { GreetingSection } from '../GreetingSection/GreetingSection';
import { NoAccessOrganizationList } from '../NoAccessOrganizationList/NoAccessOrganizationList';
import { OrganizationBox } from '../OrganizationBox/OrganizationBox';
import { OrganizationOwners } from '../OrganizationOwners/OrganizationOwners';
import { useCommonAuthProvider } from '@modules/auth/providers/CommonAuthProvider/CommonAuthProvider';
import { useFlagsProxy } from '@modules/feature-flags/hooks/useFlagsProxy';
import { CustomSpin } from '@modules/shared/components/CustomSpin';
import { getCookie } from '@utils/CookieUtil';
import { COOKIE_NAMES } from '@utils/constants';
import { useUser } from 'app/_providers/User';
import { OrganizationSelectorTabs } from 'app/_utils/data/types';

import { SearchOutlined } from '@ant-design/icons';
import { Spinner } from '@capra/core';
import { Button, Input } from 'antd';
import { useSearchParams } from 'next/navigation';
import * as React from 'react';
import { isOrgOwner } from 'shared/src/authz/validators';
import { Organization } from 'shared/src/organization/types';
import { Membership } from 'shared/src/types';

export const OrganizationSelection: React.FC = () => {
  const { user, hasIdpManagedPermissions } = useCommonAuthProvider();
  const {
    cloudOrganizationsMaxOwned,
    cloudAllowOrgCreation,
    cloudOrganizationsAllowMultiOrgCreation,
  } = useFlagsProxy();
  const { memberships } = useUser();
  const [createOrg, setCreateOrg] = React.useState(false);
  const [canCreate, setCanCreate] = React.useState(false);
  const [organization, setOrganization] = React.useState<Organization | undefined>();
  const [filter, setFilter] = React.useState('');
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [selectedTab, setSelectedTab] = React.useState<OrganizationSelectorTabs>(
    OrganizationSelectorTabs.ORGANIZATIONS,
  );
  const link = getCookie(COOKIE_NAMES.CRIBL_DOCS);

  const searchParams = useSearchParams();

  const isOpen = (): void => {
    setIsModalOpen(true);
    setCreateOrg(true);
  };

  const handleCloseModal = (): void => {
    setIsModalOpen(false);
    setCreateOrg(false);
  };

  React.useEffect(() => {
    if (!memberships.data) {
      return;
    }

    const ownedOrgsCount = memberships.data.filter((membership) => isOrgOwner(membership.roles)).length;

    if (ownedOrgsCount >= cloudOrganizationsMaxOwned) {
      return;
    }

    if (!cloudAllowOrgCreation) {
      return;
    }

    const userIsAllowedToCreateOrg =
      !hasIdpManagedPermissions &&
      (cloudOrganizationsAllowMultiOrgCreation || memberships.data.length === 0);

    if (userIsAllowedToCreateOrg) {
      setCanCreate(true);
    }

    if (memberships.data.length === 0 && userIsAllowedToCreateOrg) {
      setIsModalOpen(true);
    }
  }, [memberships.data, cloudOrganizationsMaxOwned, cloudAllowOrgCreation, cloudOrganizationsAllowMultiOrgCreation]);

  React.useEffect(() => {
    const tab = searchParams?.get('tab');

    if (tab === OrganizationSelectorTabs.NOTIFICATIONS) {
      setSelectedTab(tab);
    }
  }, [searchParams]);

  const handleFilter = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setFilter(e.target.value);
  };

  const handleShowOwners = (org: Organization): void => {
    setOrganization(org);
  };

  const hasTenants = Array.isArray(memberships.data) && memberships.data.length > 0;
  const hasNoAccess = !hasTenants && !canCreate && cloudAllowOrgCreation;

  if (memberships.data === undefined) {
    return (
      <Styled.LoaderContainer>
        <Spinner size='lg' />
      </Styled.LoaderContainer>
    );
  }

  if (hasNoAccess) {
    return <NoAccessOrganizationList />;
  }

  const shouldShowCreateOrganizationModal = (createOrg || (memberships.data && !hasTenants)) &&
    isModalOpen &&
    selectedTab !== OrganizationSelectorTabs.NOTIFICATIONS;

  if (shouldShowCreateOrganizationModal) {
    return (
      <CreateOrganization
        isOpen={isModalOpen || memberships.data?.length === 0}
        onClose={handleCloseModal}
      />
    );
  }

  return (
    <Styled.BaseOrganizationDiv>
      {organization && (
        <OrganizationOwners
          organization={organization}
          handleCancel={() => setOrganization(undefined)}
        />
      )}

      {/* Redirect Modal if there is a criblDoc link */}
      {link && (
        <OrganizationRedirectModal
          link={link}
          memberships={memberships.data}
        />
      )}

      <Styled.SearchHeaderRow>
        {hasTenants && (
          <Styled.SearchInput>
            <Input
              placeholder='Search for an Organization'
              allowClear
              prefix={<SearchOutlined />}
              data-testid='organizations-search-input'
              onChange={handleFilter}
            />
          </Styled.SearchInput>)}

        {canCreate && (
          <Styled.AddButton>
            <Button
              type='primary'
              onClick={isOpen}
            >
              Add Organization
            </Button>
          </Styled.AddButton>
        )}
      </Styled.SearchHeaderRow>

      <Styled.ContentContainer>
        <GreetingSection
          userName={user?.given_name}
          canCreate={canCreate}
          showAlert={!cloudAllowOrgCreation && !hasTenants}
        />

        <Styled.OrganizationsRow>
          {memberships.isLoading && (
            <Styled.LoadingCol span={24}>
              <CustomSpin />
            </Styled.LoadingCol>
          )}
          {!memberships.isLoading && memberships.data?.map((membership: Membership) => {
            const filterRegExp = new RegExp(filter, 'ig');

            if (
              !filter ||
              filterRegExp.test(membership.tenant) ||
              filterRegExp.test(membership.organization.organizationAlias as string)
            ) {
              return (
                <Styled.OrganizationCol
                  xs={24}
                  key={membership.tenant}
                >
                  <OrganizationBox
                    key={`${membership.tenant}-content`}
                    membership={membership}
                    handleShowOwners={handleShowOwners}
                    fetchMemberships={memberships.mutate}
                  />
                </Styled.OrganizationCol>
              );
            }

            return null;
          })}
        </Styled.OrganizationsRow>
      </Styled.ContentContainer>
    </Styled.BaseOrganizationDiv>
  );
};
